import { aiClient as _aiClient, aiStream } from '@api/sdk-client';
import { IResumeRequest } from '@models/jooice-ai/jooice-ai';
import {
  IConversation,
  IConversationDetail,
  ICreditBalance,
  IExtractInfoResponse,
  ISocialPostResponse,
  ISuggestedPublisher,
  ISuggestScheduleResponse,
} from '@models/jooice-ai/response';
import { DEFAULT_TEXT_PLATFORM_SELECTOR, ESocialPlatforms } from '@models/social/social';
import {
  chatControllerCreateConversationV1,
  chatControllerDeleteConversationV1,
  chatControllerGetConversationV1,
  chatControllerListConversationsV1,
  chatControllerUpdateDisplayNameV1,
  creditControllerGetBalanceV1,
  generationControllerBusinessDescriptionV1,
  generationControllerImageV1,
  generationControllerShareReviewWithoutSensitiveDataV1,
  generationControllerSocialPostV1,
  generationControllerSuggestedPublishersV1,
  generationControllerSuggestScheduleV1,
  generationControllerTranslateV1,
  onboardingControllerExtractInfoV1,
  reviewControllerExtractSourceUrlV1,
  reviewControllerGenerateReviewRequestV1,
  reviewControllerSubmitFeedbackV1,
  ShareReviewWithoutSensitiveDataRequest,
  SourceUrlRequest,
} from '@webidoo-eng/jooice-ai';
import i18next from 'i18next';

/**
 * Platform ids the generation endpoints accept. They call the Google Business channel 'google',
 * while the frontend calls it 'gmb' — sending the frontend id fails request validation, and the
 * caller is left to fall back (scheduling ends up on "now" instead of the suggested slot).
 */
type GenerationPlatformId =
  | 'bluesky'
  | 'facebook'
  | 'google'
  | 'instagram'
  | 'linkedin'
  | 'pinterest'
  | 'reddit'
  | 'snapchat'
  | 'telegram'
  | 'threads'
  | 'tiktok'
  | 'twitter'
  | 'x'
  | 'youtube';

/**
 * The generation endpoints require a non-empty brand name. Callers read it from context that may
 * still be loading (ScheduleCard sends `currentBrand?.name || ''`), and an empty one fails request
 * validation — which the schedule flow reports as "publish now". A neutral placeholder keeps the
 * request valid; brand name is only prompt context, so the suggestion is still worth having.
 */
const FALLBACK_BRAND_NAME = 'Brand';

const toBrandName = (brandName: string): string => brandName.trim() || FALLBACK_BRAND_NAME;

const toGenerationPlatformIds = (platforms: string[]): GenerationPlatformId[] =>
  platforms.map((platform) => (platform === ESocialPlatforms.GMB ? 'google' : platform)) as GenerationPlatformId[];

/** Optional org / brand / location context for post generation. */
export interface IGeneratePostTextContext {
  organizationId?: number;
  brandId?: number;
  locationId?: number;
}

// The SDK generates its own client under dist/client, so its `Client` type is a separate
// declaration from the one the top-level @hey-api/client-axios returns — structurally identical,
// nominally distinct, hence unassignable. Building the AI client from the SDK's own createClient
// in sdk-client.ts would remove the need for this cast.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const aiClient = _aiClient as any;

async function readSseContent(stream: ReadableStreamDefaultReader<Uint8Array>): Promise<string> {
  const decoder = new TextDecoder();
  let sseBuffer = '';
  let currentEvent = 'content';
  let accumulatedText = '';

  while (true) {
    const { done, value } = await stream.read();
    if (done) break;

    sseBuffer += decoder.decode(value, { stream: true });
    const frames = sseBuffer.split('\n\n');
    sseBuffer = frames.pop() ?? '';

    for (const frame of frames) {
      for (const line of frame.split('\n')) {
        if (line.startsWith('event: ')) {
          currentEvent = line.slice(7).trim();
          continue;
        }

        if (!line.startsWith('data: ') || currentEvent !== 'content') continue;
        accumulatedText += JSON.parse(line.slice(6)).content ?? '';
      }
    }
  }

  return accumulatedText.trim();
}

// ---------------------------------------------------------------------------
// Streaming request body (matches new SendMessageDto)
// ---------------------------------------------------------------------------
export interface IStreamRequest {
  chatId: string;
  message: string;
  language?: string;
  organizationId?: number;
  brandId?: number;
  locationId?: number;
}

// ---------------------------------------------------------------------------
// Jooice AI API facade — backed by @webidoo-eng/jooice-ai SDK
// ---------------------------------------------------------------------------
const jooiceAiApi = {
  promptStream: async (body: IStreamRequest, searchTool?: boolean) => {
    const endPoint = searchTool ? '/v1/search' : '/v1/chat';
    return aiStream(endPoint, body);
  },

  findChats: async (): Promise<IConversation[]> => {
    const { data } = await chatControllerListConversationsV1({
      client: aiClient,
    });
    return (data ?? []) as IConversation[];
  },

  findOneChat: async (chatId: string): Promise<IConversationDetail> => {
    const { data } = await chatControllerGetConversationV1({
      client: aiClient,
      path: { chatId },
    });
    return data as IConversationDetail;
  },

  aiRemainingCredits: async (): Promise<ICreditBalance> => {
    const { data } = await creditControllerGetBalanceV1({
      client: aiClient,
    });
    return data as ICreditBalance;
  },

  sendFeedback: async (body: {
    runId: string;
    feedbackType: 'thumbsUp' | 'doubleThumbsUp' | 'thumbsDown';
    comment?: string;
  }) => {
    const { data } = await reviewControllerSubmitFeedbackV1({
      client: aiClient,
      body,
    });
    return data;
  },

  renameChat: async (chatId: string, displayName: string) => {
    await chatControllerUpdateDisplayNameV1({
      client: aiClient,
      path: { chatId },
      body: { displayName },
    });
  },

  deleteChat: async (chatId: string) => {
    await chatControllerDeleteConversationV1({
      client: aiClient,
      path: { chatId },
    });
  },

  newChat: async (displayName = 'New Chat'): Promise<{ chatId: string }> => {
    const { data } = await chatControllerCreateConversationV1({
      client: aiClient,
      body: { displayName },
    });
    return data as { chatId: string };
  },

  extractInfo: async (query: string): Promise<IExtractInfoResponse> => {
    const { data } = await onboardingControllerExtractInfoV1({
      client: aiClient,
      body: { query },
      headers: { 'accept-language': i18next.language, authorization: '' },
    });
    return data as IExtractInfoResponse;
  },

  getWebsiteInfo: async (websiteUrl: string): Promise<IExtractInfoResponse> => {
    return jooiceAiApi.extractInfo(websiteUrl);
  },

  getImprovedBusinessDescription: async ({
    message,
    businessName,
  }: {
    message: string;
    businessName?: string;
  }): Promise<{ improvedBusinessDescription: string }> => {
    const { data } = await generationControllerBusinessDescriptionV1({
      client: aiClient,
      body: {
        descriptionContent: message,
        businessName: businessName ?? '',
      },
    });
    return data as { improvedBusinessDescription: string };
  },

  getReviewSourceUrl: async (publisherName: string, businessName: string, businessAddress: string) => {
    const { data } = await reviewControllerExtractSourceUrlV1({
      client: aiClient,
      // The endpoint searches the publisher's own domain for this business, so it needs the three
      // fields apart — publisherName doubles as the domain (e.g. 'trustpilot.com') and the address
      // tells same-name businesses apart.
      body: {
        publisher: publisherName,
        name: businessName,
        address: businessAddress,
      } as unknown as SourceUrlRequest,
    });
    return data as { title: string; content: string; url: string };
  },

  getTranslatedText: async (message: string, targetLanguage: string): Promise<{ translatedText: string }> => {
    const { data } = await generationControllerTranslateV1({
      client: aiClient,
      body: { message, targetLanguage },
    });
    return data as { translatedText: string };
  },

  generateImage: async (message: string): Promise<string> => {
    const { data } = await generationControllerImageV1({
      client: aiClient,
      body: { message },
    });
    return (data as { url?: string })?.url ?? '';
  },

  suggestPublishers: async (body: {
    jooiceDomain: string;
    country: string;
    organization: string;
    availablePublishers: string;
  }): Promise<{ suggestedPublishers: ISuggestedPublisher[] }> => {
    const { data } = await generationControllerSuggestedPublishersV1({
      client: aiClient,
      // Required by the endpoint: publisher.service uses it to pick the response language.
      headers: { 'accept-language': i18next.language },
      query: body,
    });
    return data as { suggestedPublishers: ISuggestedPublisher[] };
  },

  social: {
    generatePostText: async (
      message: string,
      platforms: ESocialPlatforms[],
      businessName?: string,
      maxOutputLength?: number,
      desiredPostLength?: number,
      _context?: IGeneratePostTextContext,
    ): Promise<ISocialPostResponse> => {
      // Map FE platforms to the generation endpoint's ids (GMB -> google). Empty selection falls
      // back to a single concrete platform: the synthetic 'all' channel makes the model emit a
      // nested JSON blob instead of a caption, so it must never be sent here.
      const platformIds = toGenerationPlatformIds(platforms.length > 0 ? platforms : [ESocialPlatforms.Facebook]);
      const { data } = await generationControllerSocialPostV1({
        client: aiClient,
        headers: { 'accept-language': i18next.language },
        body: {
          message,
          socialMediaPlatforms: platformIds,
          ...(businessName ? { businessName } : {}),
          language: i18next.language,
          ...(maxOutputLength != null ? { maxOutputLength } : {}),
          ...(desiredPostLength != null ? { desiredPostLength } : {}),
        },
      });
      // Endpoint returns one caption per requested platform. Preserve the legacy contract: a single
      // caption under both keys the callers read.
      const captions = (data ?? {}) as Record<string, string>;
      const reply = (captions[platformIds[0]] ?? Object.values(captions)[0] ?? '').trim();
      return {
        [ESocialPlatforms.Facebook]: reply,
        [DEFAULT_TEXT_PLATFORM_SELECTOR]: reply,
      } as unknown as ISocialPostResponse;
    },

    getSuggestedScheduleDate: async (postData: {
      brandName: string;
      platforms: string[];
      type: string;
      post: string;
      mediaUrls?: string[];
      timezone: string;
    }): Promise<ISuggestScheduleResponse> => {
      const { data } = await generationControllerSuggestScheduleV1({
        client: aiClient,
        body: {
          brandName: toBrandName(postData.brandName),
          platforms: toGenerationPlatformIds(postData.platforms),
          type: postData.type,
          post: postData.post,
          mediaUrls: postData.mediaUrls,
          timezone: postData.timezone,
        },
      });
      return data as ISuggestScheduleResponse;
    },
  },

  resumeStream: async (body: IResumeRequest) => {
    return aiStream('/v1/chat/resume', body);
  },

  promptReview: async (body: {
    reviewId: string;
    authorName: string;
    rating: number;
    reviewText: string;
    tone?: string;
    language?: string;
  }) => {
    return aiStream('/v1/review/prompt', body);
  },

  reputation: {
    generateRequestText: async (message: string, businessName?: string): Promise<{ text: string }> => {
      const { data } = await reviewControllerGenerateReviewRequestV1({
        client: aiClient,
        body: {
          businessName: businessName ?? message,
          language: undefined,
        },
      });
      return data as { text: string };
    },
    generateReviewReply: async (params: {
      authorName: string;
      rating: number;
      reviewText: string;
      tone: string;
    }): Promise<string> => {
      // /v1/review/prompt is the purpose-built single-shot review responder (SSE). reviewId is
      // required by the endpoint but only used server-side for usage logging.
      const stream = await aiStream('/v1/review/prompt', {
        reviewId: 'ai-rewrite',
        authorName: params.authorName,
        rating: params.rating,
        reviewText: params.reviewText,
        ...(params.tone ? { tone: params.tone } : {}),
        language: i18next.language?.split('-')[0],
      });
      if (!stream) return '';
      const reply = await readSseContent(stream);
      stream.releaseLock();
      return reply;
    },
    shareReviewWithoutSensitive: async (body: ShareReviewWithoutSensitiveDataRequest) => {
      const { data } = await generationControllerShareReviewWithoutSensitiveDataV1({
        client: aiClient,
        body,
      });
      return data as { text: string };
    },
  },
};

export default jooiceAiApi;
